import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useSessionStore } from '@/lib/auth/session-store'

import {
  NativePushError,
  disableNativePush,
  enableNativePush,
  listenNativePushReceived,
  listenNativePushTapped,
  nativePushEnabledLocally,
  nativePushPermissionGranted,
  nativePushSupported,
  syncNativePush,
} from '../push/native-push'

export type NativePushStatus = {
  tone: 'success' | 'error'
  messageKey: string
} | null

export interface NativePushState {
  supported: boolean
  permission: 'granted' | 'denied' | 'prompt' | 'unsupported'
  subscribed: boolean
  busy: 'enabling' | 'disabling' | null
  status: NativePushStatus
  enable: () => Promise<void>
  disable: () => Promise<void>
  dismissStatus: () => void
}

/** Device-level native push state for the notifications settings card. */
export function useNativePush(): NativePushState {
  const supported = nativePushSupported()
  const [permission, setPermission] = useState<NativePushState['permission']>(
    supported ? 'prompt' : 'unsupported',
  )
  const [subscribed, setSubscribed] = useState(false)
  const [busy, setBusy] = useState<NativePushState['busy']>(null)
  const [status, setStatus] = useState<NativePushStatus>(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    if (!supported) return
    void nativePushPermissionGranted().then((granted) => {
      if (!mountedRef.current) return
      setPermission(granted ? 'granted' : 'prompt')
      setSubscribed(granted && nativePushEnabledLocally())
    })
    return () => {
      mountedRef.current = false
    }
  }, [supported])

  const enable = useCallback(async () => {
    if (busy || !supported) return
    setBusy('enabling')
    setStatus(null)
    try {
      await enableNativePush()
      if (!mountedRef.current) return
      setPermission('granted')
      setSubscribed(true)
      setStatus({
        tone: 'success',
        messageKey: 'notifications.nativePush.enableSuccess',
      })
    } catch (error) {
      if (!mountedRef.current) return
      if (error instanceof NativePushError && error.code === 'denied') {
        setPermission('denied')
      } else {
        setStatus({
          tone: 'error',
          messageKey: 'notifications.nativePush.enableError',
        })
      }
    } finally {
      if (mountedRef.current) setBusy(null)
    }
  }, [busy, supported])

  const disable = useCallback(async () => {
    if (busy || !supported) return
    setBusy('disabling')
    setStatus(null)
    try {
      await disableNativePush()
      if (!mountedRef.current) return
      setSubscribed(false)
      setStatus({
        tone: 'success',
        messageKey: 'notifications.nativePush.disableSuccess',
      })
    } catch {
      if (!mountedRef.current) return
      setStatus({
        tone: 'error',
        messageKey: 'notifications.nativePush.disableError',
      })
    } finally {
      if (mountedRef.current) setBusy(null)
    }
  }, [busy, supported])

  return {
    supported,
    permission,
    subscribed,
    busy,
    status,
    enable,
    disable,
    dismissStatus: () => setStatus(null),
  }
}

/**
 * App-level native push lifecycle: re-register the FCM token after login and
 * route notification taps (including the cold-start tap) into the app.
 */
export function useNativePushLifecycle(): void {
  const navigate = useNavigate()
  const status = useSessionStore((state) => state.status)

  useEffect(() => {
    if (!nativePushSupported()) return
    let cancelled = false
    const listeners: Array<() => void> = []
    // Register the tap listener first: the first listener arms native
    // delivery and drains the cold-start tap queue, so the tap channel must
    // already exist. Foreground receives that arrived before this point are
    // not presented anyway; the app is already active.
    void listenNativePushTapped((notification) => {
      const target = notification.data?.url
      if (target && target.startsWith('/') && !target.startsWith('//')) {
        navigate(target)
      }
    }).then((listener) => {
      if (cancelled) {
        void listener?.unregister()
      } else if (listener) {
        listeners.push(() => void listener.unregister())
      }
    })
    void listenNativePushReceived(() => {
      // A foreground push is intentionally not shown as a system
      // notification; the chat/notification surfaces already update.
    }).then((listener) => {
      if (cancelled) {
        void listener?.unregister()
      } else if (listener) {
        listeners.push(() => void listener.unregister())
      }
    })
    return () => {
      cancelled = true
      for (const unregister of listeners.splice(0)) unregister()
    }
  }, [navigate])

  useEffect(() => {
    if (status !== 'authenticated' || !nativePushSupported()) return
    void syncNativePush()
  }, [status])
}
